import React, { useRef, useEffect, useState } from 'react';
import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import { setHeadingSize } from 'lib/sitecore/component-params';
import { getEnumValue } from 'lib/util/enum';
import { useIsNRC } from 'lib/util/site-configuration';
import { classnames } from 'tailwindcss-classnames';
import QuoteDown from './SvgQuotes/quote-down';
import QuoteUp from './SvgQuotes/quote-up';

export type PullQuoteProps = Sitecore.Override.ComponentBase &
  Components.General.PullQuote.Fields.PullQuote;

const fontStyle = `font-semibold`;

const PullQuote = ({ fields }: PullQuoteProps): JSX.Element => {
  const pullQuoteRef = useRef<HTMLDivElement>(null);
  const [isInGrid, setIsInGrid] = useState(false);

  useEffect(() => {
    if (pullQuoteRef.current) {
      let parent = pullQuoteRef.current.parentElement;
      for (let index = 0; index < 3; index++) {
        if (parent) {
          if (
            parent.classList.contains('grid') ||
            parent.classList.contains('md:grid') ||
            parent.classList.contains('lg:grid')
          ) {
            setIsInGrid(true);
            break;
          }
          parent = parent.parentElement;
        }
      }
    }
  }, []);

  const isNRCVal = useIsNRC();
  if (!fields) {
    return <MissingDataSource />;
  }

  const paddingY = isInGrid ? classnames('py-2') : classnames('py-10', 'md:py-20');
  type HeadingSizes = 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl';
  const headingSize: HeadingSizes | undefined =
    getEnumValue<HeadingSizes>(fields.quoteFontSize) || undefined;
  const headingClasses = classnames(setHeadingSize(headingSize), fontStyle);
  const headingClassesNRC = classnames(
    setHeadingSize(headingSize),
    fontStyle,
    'leading-9',
    'before:absolute',
    'before:content-["“"]',
    'md:before:text-6xxl',
    'before:text-4xl',
    'before:left-[24px]',
    'before:top-0',
    'md:before:top-[-2px]',
    'before:leading-11',
    'md:before:leading-13',
    'before:h-[38px]',
    'before:w-[15px]',
    'md:before:h-[54px]',
    'md:before:w-[24px]',
    'before:text-black',
    'before:top-0',
    'md:before:left-[24px]',
    'before:left-[8px]'
  );

  const quoteContainer = classnames(
    'max-w-screen-2xl',
    'mx-auto',
    'w-full',
    'block',
    'relative',
    'px-17',
    paddingY,
    'md:px-24',
    'overflow-hidden',
    'bg-blue-navy',
    'text-white',
    'text-center',
    'pull-quote'
  );
  const quoteContainerNRC = classnames(
    'max-w-centerContainer',
    'mx-auto',
    'w-full',
    'block',
    'relative',
    paddingY,
    'overflow-hidden',
    'text-black',
    'pull-quote'
  );
  const middleText = classnames('text-m', 'leading-4', fontStyle);
  const middleTextNRC = classnames(
    'text-body',
    'leading-5',
    'uppercase',
    'font-bold',
    'mt-4',
    'tracking-[1.44px]'
  );
  const additionalText = classnames('pt-2', 'leading-3');

  const gradientHr = classnames(
    'w-36',
    'h-1',
    'mx-auto',
    'my-4',
    'border-0',
    'bg-gradient-to-r',
    'from-blue',
    'to-green'
  );
  const gradientHrNRC = classnames(
    'max-w-screen-2xl',
    'h-1',
    'mx-auto',
    'my-4',
    'border-t-8',
    'border-green'
  );

  return (
    <div
      ref={pullQuoteRef}
      data-component="authorable/general/pullquote"
      className={classnames('pull-quote', { 'rounded-xl': isInGrid })}
    >
      {isNRCVal ? (
        <>
          <div className={classnames(quoteContainerNRC)}>
            <hr className={classnames(gradientHrNRC)} />
            <div className="relative pl-10 pr-6 md:pl-16 md:pr-3">
              <Text className={headingClassesNRC} field={fields.quote} tag="p" />
              <Text
                className={classnames(middleTextNRC)}
                field={fields.attributionField1}
                tag="p"
              />
              <Text
                className={classnames(additionalText)}
                field={fields.attributionField2}
                tag="p"
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={classnames(quoteContainer)}>
            <Text className={headingClasses} field={fields.quote} tag="p" />
            <hr className={classnames(gradientHr)} />
            <Text className={classnames(middleText)} field={fields.attributionField1} tag="p" />
            <Text className={classnames(additionalText)} field={fields.attributionField2} tag="p" />
            <QuoteUp />
            <QuoteDown />
          </div>
        </>
      )}
    </div>
  );
};

export default PullQuote;
